import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export default function useStreamVisualizer({
  tokenLength = 32,
  capacityKB = 1024,
  charOptions = { lowercase: true, uppercase: true, numbers: true, symbols: false },
  running = true,
  speedFill = 1.0,       // vitesse cube bleu
  speedOutline = 1.0,    // vitesse cube vert
  speedMultiplier = 1.0, // multiplicateur global
  onTokensUpdate = () => {},
  onEntropyUpdate = () => {},
}) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);

  const fillMeshRef = useRef(null);     // cube bleu (contenu)
  const outlineMeshRef = useRef(null);  // cube vert (fil)

  const velocityFillRef = useRef(new THREE.Vector3(
    (Math.random() - 0.5) * 0.2,
    (Math.random() - 0.5) * 0.2,
    (Math.random() - 0.5) * 0.2
  ));
  const velocityOutlineRef = useRef(new THREE.Vector3(
    (Math.random() - 0.5) * 0.1,
    (Math.random() - 0.5) * 0.1,
    (Math.random() - 0.5) * 0.1
  ));

  const animationIdRef = useRef(null);
  const tokensBufferRef = useRef([]);

  useEffect(() => {
    if (!containerRef.current) return;

    // --- Scene ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    // --- Camera ---
    const camera = new THREE.PerspectiveCamera(
      60,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 10, 35);
    scene.add(camera);

    // --- Renderer ---
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);

    // --- OrbitControls ---
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.enableRotate = true;

    // --- Lights ---
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 15, 10);
    scene.add(dirLight);

    // --- Cube filaire vert ---
    const outlineGeometry = new THREE.BoxGeometry(20, 15, 5);
    const outlineMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      wireframe: true,
      transparent: true,
      opacity: 0.5,
    });
    const outlineMesh = new THREE.Mesh(outlineGeometry, outlineMaterial);
    scene.add(outlineMesh);

    // --- Cube bleu (démarre vide) ---
    const capacityScale = Math.cbrt(capacityKB / 1024);
    const fillGeometry = new THREE.BoxGeometry(6 * capacityScale, 6, 4);
    const fillMaterial = new THREE.MeshPhongMaterial({ color: 0x0077ff, emissive: 0x0033aa });
    const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial);
    fillMesh.scale.y = 0.001;
    fillMesh.position.y = -fillGeometry.parameters.height / 2;
    scene.add(fillMesh);

    // Save refs
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    fillMeshRef.current = fillMesh;
    outlineMeshRef.current = outlineMesh;

    // --- Resize ---
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      // cleanup OrbitControls
      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = null;
      }

      // cleanup scene
      if (sceneRef.current) {
        sceneRef.current.traverse(obj => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(m => m.dispose && m.dispose());
            } else {
              obj.material.dispose && obj.material.dispose();
            }
          }
        });
        sceneRef.current.clear();
        sceneRef.current = null;
      }

      // cleanup renderer
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (rendererRef.current.domElement && containerRef.current) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current = null;
      }

      cancelAnimationFrame(animationIdRef.current);
    };
  }, [capacityKB]);

  // --- Animation loop ---
  useEffect(() => {
    if (!running) {
      cancelAnimationFrame(animationIdRef.current);
      return;
    }

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      const scene = sceneRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      const controls = controlsRef.current;
      const fillMesh = fillMeshRef.current;
      const outlineMesh = outlineMeshRef.current;

      if (scene && camera && renderer && fillMesh && outlineMesh) {
        // mouvements cubes
        fillMesh.position.addScaledVector(velocityFillRef.current, speedFill * speedMultiplier);
        ["x", "y", "z"].forEach(axis => {
          if (Math.abs(fillMesh.position[axis]) > { x: 12, y: 9, z: 3 }[axis]) {
            velocityFillRef.current[axis] *= -1;
          }
        });
        fillMesh.rotation.x += 0.02 * speedFill * speedMultiplier;
        fillMesh.rotation.y += 0.015 * speedFill * speedMultiplier;

        outlineMesh.position.addScaledVector(velocityOutlineRef.current, speedOutline * speedMultiplier);
        ["x", "y", "z"].forEach(axis => {
          if (Math.abs(outlineMesh.position[axis]) > { x: 14, y: 10, z: 4 }[axis]) {
            velocityOutlineRef.current[axis] *= -1;
          }
        });
        outlineMesh.rotation.x += 0.01 * speedOutline * speedMultiplier;
        outlineMesh.rotation.y += 0.02 * speedOutline * speedMultiplier;

        // volume
        const totalBytes = tokensBufferRef.current.reduce((sum, t) => sum + t.length, 0);
        const cappedBytes = Math.min(totalBytes, capacityKB * 1024);
        const volumeKB = Math.round(cappedBytes / 1024);
        const fillRatio = Math.min(1, volumeKB / capacityKB);
        fillMesh.scale.y = Math.max(0.001, fillRatio);
        fillMesh.position.y = -fillMesh.geometry.parameters.height / 2 +
          (fillMesh.geometry.parameters.height / 2) * fillMesh.scale.y;

        // entropies
        const avgTokenLen = tokenLength;
        const optionsCount = Object.values(charOptions).filter(Boolean).length;
        const entropyUI =
          (avgTokenLen * optionsCount * 0.001) * (1 + fillRatio) +
          (Math.random() - 0.5) * 0.02;

        const dynBlue =
          velocityFillRef.current.length() * speedFill * speedMultiplier +
          Math.abs(fillMesh.rotation.x + fillMesh.rotation.y);

        const dynGreen =
          velocityOutlineRef.current.length() * speedOutline * speedMultiplier +
          Math.abs(outlineMesh.rotation.x + outlineMesh.rotation.y);

        const noise = (Math.random() - 0.5) * 0.05;
        const entropyDyn = ((dynBlue + dynGreen) * 0.01 * (1 + fillRatio)) + noise;

        onEntropyUpdate({
          entropy_ui: entropyUI,
          entropy_dyn: entropyDyn,
          volume_kb: volumeKB,
        });

        if (controls) controls.update();
        renderer.render(scene, camera);
      }
    };

    animate();

    return () => cancelAnimationFrame(animationIdRef.current);
  }, [running, speedFill, speedOutline, speedMultiplier, tokenLength, charOptions, capacityKB]);

  // --- Capture tokens ---
  useEffect(() => {
    let active = true;
    const fetchLoop = async () => {
      if (!running) return;
      try {
        const res = await fetch("/api/geometry/stream/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            length: tokenLength,
            capacity_bytes: capacityKB * 1024,
            char_options: charOptions,
          }),
        });
        const data = await res.json();
        if (data.tokens) {
          tokensBufferRef.current.push(...data.tokens);
          onTokensUpdate(data.tokens);
        }
      } catch (err) {
        console.error("Erreur fetch tokens:", err);
      }
      if (active) requestAnimationFrame(fetchLoop);
    };

    fetchLoop();
    return () => { active = false; };
  }, [running, tokenLength, capacityKB, charOptions, onTokensUpdate]);

  return { containerRef };
}
